import {
  CognitoUserPool,
  type ICognitoUserPoolData,
  CognitoUser,
  CognitoUserSession,
  AuthenticationDetails,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js'

import notification from '@shared/components/ui/c-notification'
import { ENotificationType } from '@shared/types/constants/notification-type'
import { EDomainScopedRoles } from '@shared/types/constants/domain-scoped-roles'

import type { EUserRole } from '@shared/api/user-management/resources/user/constants/user-roles'
import type { ICognitoUser } from '@shared/types/cognito-user'
import { MfaRequiredError, PasswordChangeRequestedError } from '@shared/errors/authorization'
import type { ISSOResponse } from '@shared/types/sso'
import { parseJwt } from '@shared/utils/helpers'
import { PostHog } from 'posthog-js'

class Authorization {
  private readonly cognitoUserPool: CognitoUserPool
  public user: ICognitoUser | null = null
  public lastAuthUser: ICognitoUser | null = null
  private userAttributes: any = null
  public userRoleList: EUserRole[] = []
  public userOrganizationList: { [key: string]: EUserRole[] } = {}
  public userLocationList: { [key: string]: EUserRole[] } = {}
  public userId = ''
  public isLoading = false
  public constructor(cognitoUserPoolData: ICognitoUserPoolData) {
    this.cognitoUserPool = new CognitoUserPool(cognitoUserPoolData)
  }

  private setUser({ username }: { username: string }): ICognitoUser {
    this.user = new CognitoUser({
      Username: username,
      Pool: this.cognitoUserPool,
    })
    return this.user
  }

  private authenticateUser({
    username,
    password,
  }: {
    username: string
    password: string
  }): Promise<CognitoUserSession> {
    const authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    return new Promise((resolve, reject) => {
      this.user!.authenticateUser(authenticationDetails, {
        onSuccess: resolve,
        onFailure: e => {
          notification.open({
            message: e.message,
            type: ENotificationType.ERROR,
          })
          reject(e)
        },
        newPasswordRequired: (userAttributes): void => {
          delete userAttributes.email_verified
          delete userAttributes.email
          this.userAttributes = userAttributes
          throw new PasswordChangeRequestedError()
        },
        mfaRequired: (): void => {
          throw new MfaRequiredError()
        },
      })
    })
  }

  public async login({ username, password }: { username: string; password: string }, posthog?: PostHog): Promise<void> {
    this.setUser({ username })
    const session = await this.authenticateUser({ username, password })
    const payload = session.getIdToken().payload
    if (posthog && payload) {
      posthog.identify(payload.userId, {
        email: payload.email,
        first_name: payload.family_name,
        last_name: payload.given_name,
      })
    }
  }

  public logout(posthog?: PostHog): void {
    if (this.user) {
      this.user.signOut()
      this.user = null
      this.userAttributes = null
      if (posthog) {
        posthog.reset()
      }
    }
    localStorage.removeItem('customToken')
    location.reload()
  }

  setUserIdAndRoles(sessionToken: string): void {
    const parsedJwt = parseJwt(sessionToken)
    this.userId = parsedJwt.userId
    const domainScopedRoles: {
      [key in keyof typeof EDomainScopedRoles]: { [key: string]: EUserRole[] }
    } = JSON.parse(parsedJwt!.permissions).domainScopedRoles
    this.userRoleList = [
      ...new Set(
        Object.values(domainScopedRoles)
          .map(el => Object.values(el))
          .flat(2)
      ),
    ]
    this.userOrganizationList = domainScopedRoles[EDomainScopedRoles.ORGANIZATION]
    this.userLocationList = domainScopedRoles[EDomainScopedRoles.COMMUNITY]
  }

  public async loadPersisted(): Promise<CognitoUserSession | null> {
    const customToken = localStorage.getItem('customToken') || null
    if (customToken) {
      this.setUserIdAndRoles(customToken)
    }
    if (!this.user) {
      this.user = this.cognitoUserPool.getCurrentUser()
      return this.user ? await this.getSession() : Promise.resolve(null)
    }
    return await this.getSession()
  }

  private getSession(): Promise<CognitoUserSession | null> {
    return new Promise(resolve => {
      this.user!.getSession((error: Error | null, session: null | CognitoUserSession) => {
        if (error instanceof Error) {
          this.lastAuthUser = this.user
          resolve(null)
        }
        const customToken = localStorage.getItem('customToken') || null
        const sessionToken: string | null = session ? session.getIdToken().getJwtToken() : customToken
        if (sessionToken) {
          this.setUserIdAndRoles(sessionToken)
        }
        resolve(session)
      })
    })
  }

  async ssoAuth({ id_token, access_token, refresh_token }: ISSOResponse): Promise<void> {
    const IdToken = new CognitoIdToken({
      IdToken: id_token,
    })
    const AccessToken = new CognitoAccessToken({
      AccessToken: access_token,
    })
    const RefreshToken = new CognitoRefreshToken({
      RefreshToken: refresh_token,
    })
    const userData = IdToken.decodePayload()
    this.setUser({ username: userData.email })
    const cognitoUserSession = new CognitoUserSession({
      IdToken,
      AccessToken,
      RefreshToken,
    })
    return await this.user!.setSignInUserSession(cognitoUserSession)
  }

  async refreshSession(): Promise<CognitoUserSession | undefined> {
    this.isLoading = true
    this.user = this.cognitoUserPool.getCurrentUser()

    if (this.user === null) {
      return
    }

    const session: CognitoUserSession | null = await this.getSession()
    if (session) {
      const refreshToken: CognitoRefreshToken = session.getRefreshToken()
      this.user!.refreshSession(refreshToken, (err: Error | null, session: null | CognitoUserSession) => {
        this.isLoading = false
        if (err) {
          this.logout()
        }
        return session
      })
    }
  }

  completeNewPasswordChallenge({ newPassword }: { newPassword: string }) {
    return new Promise((resolve, reject) => {
      this.user!.completeNewPasswordChallenge(newPassword, this.userAttributes, {
        onSuccess: resolve,
        onFailure: e => {
          notification.open({
            message: e.message,
            type: ENotificationType.ERROR,
          })
          reject(e)
        },
      })
    })
  }

  public changePassword({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      this.user!.changePassword(oldPassword, newPassword, (e): void => {
        if (e) {
          notification.open({
            message: e.message,
            type: ENotificationType.ERROR,
          })
          reject(e)
        }
        resolve()
      })
    })
  }

  public forgotPassword({ username }: { username: string }): Promise<unknown> {
    this.setUser({ username })

    return new Promise((resolve, reject) => {
      this.user!.forgotPassword({
        onSuccess: resolve,
        onFailure: e => {
          notification.open({
            message: e.message,
            type: ENotificationType.ERROR,
          })
          reject(e)
        },
      })
    })
  }

  confirmPassword({
    verificationCode,
    newPassword,
    username,
  }: {
    verificationCode: string
    newPassword: string
    username: string
  }): Promise<unknown> {
    this.setUser({ username })
    const confirmPasswordPromise = new Promise((resolve, reject) => {
      this.user!.confirmPassword(verificationCode, newPassword, {
        onSuccess: resolve,
        onFailure: e => {
          notification.open({
            message: e.message,
            type: ENotificationType.ERROR,
          })
          reject(e)
        },
      })
    })

    return Promise.resolve(confirmPasswordPromise).then(() => {
      return this.authenticateUser({ username, password: newPassword })
    })
  }

  public sendMfaCode({ confirmationCode }: { confirmationCode: string }): Promise<CognitoUserSession | unknown> {
    return new Promise((resolve, reject) => {
      this.user!.sendMFACode(confirmationCode, {
        onSuccess: resolve,
        onFailure: e => {
          notification.open({
            message: e.message,
            type: ENotificationType.ERROR,
          })
          reject(e)
        },
      })
    })
  }

  public resendConfirmationCode(): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.user!.resendConfirmationCode((e, result) => {
        if (e) {
          notification.open({
            message: e.message,
            type: ENotificationType.ERROR,
          })
          reject(e)
        }
        resolve(result)
      })
    })
  }

  public hasAccess(roleList?: EUserRole[]): boolean {
    if (!roleList?.length) {
      return true
    }
    return roleList.some(role => this.userRoleList.includes(role))
  }
}

const authorizationInstance = new Authorization({
  UserPoolId: import.meta.env.AWS_USER_POOL_ID,
  ClientId: import.meta.env.AWS_APP_CLIENT_ID,
})

export default authorizationInstance
